/**
 * ModifyDocumentPopover · Feb 2026
 *
 * A single, shared control for the "Dynamic Order Modification" flow used
 * across SO / ESO / PO / WO detail modals. On open it hits the link probe:
 *
 *   GET /api/docs/{docType}/{docId}/links
 *
 * → When `has_links === false`, the popover shows a single **Unapprove**
 *   action that reverts the doc back to Draft (each doc-type has its own
 *   endpoint — the switch below picks the right one).
 * → When `has_links === true`, the doc can no longer be unapproved. Two
 *   safer actions are exposed:
 *     · **Short Close** — closes every remaining line with one reason.
 *     · **Cancel Balance** — inline "Cancel" on each line row so a planner
 *       can drop the balance of just one line while keeping the doc live.
 *
 * All three actions require a reason (≥ 4 chars, enforced on the backend).
 * When the doc is already Short Closed the popover morphs into a
 * **Reverse Short Close** flow (whole doc + per-line qty-level restore).
 */
import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { LockOpenIcon, XCircleIcon, WarningIcon, InfoIcon, LinkSimpleIcon } from "@phosphor-icons/react";

const DOC_LABEL = { SO: "Sales Order", ESO: "Export SO", PO: "Purchase Order", WO: "Work Order" };

// Per-doc-type Unapprove endpoint (keeps the legacy contract each screen
// already exposes so we don't break existing role guards & audit hooks).
// PO-AUTH-EDIT-AMEND-H01 · Phase 2B.3 · Feb 2026 — PO removed. The legacy
// PO unapprove_po bypass has been retired (backend returns HTTP 410).
// The canonical PO correction path is now Amend Order → Draft AMD-### →
// Save → Submit for Re-authorisation → Management Authorise/Reject.
// SO / ESO / WO Unapprove branches remain unchanged.
const UNAPPROVE_PATH = {
  SO:  (id) => `/sales-orders/${id}/unauthorize`,
  ESO: (id) => `/exports/orders/${id}/unauthorise`,
  WO:  (id) => `/work-orders/${id}/unapprove`,
};

const fmtQty = (v, digits = 2) =>
  (Number(v) || 0).toLocaleString("en-IN", { maximumFractionDigits: digits, minimumFractionDigits: digits });

export default function ModifyDocumentPopover({
  docType,        // "SO" | "ESO" | "PO" | "WO"
  docId,
  docNo,          // display-only fallback until the probe lands
  open,
  onClose,        // () => void — close the modal
  onDone,         // async () => void — invoked after a TERMINAL mutation (Unapprove / Short Close) so the caller closes its parent modal & reloads
  onLineCancel,   // async () => void — invoked after each per-line Cancel Balance so the caller can refresh in-place WITHOUT closing the popover
  testidPrefix = "modify-doc",
  // IMP-1C · P-2 — action-specific visibility; each backend endpoint still enforces its own permission.
  allow = { unapprove: true, close: true },
}) {
  const [probe, setProbe] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState("");
  const [mode, setMode] = useState(null);         // "unapprove" | "short-close" | { line_key }
  const [lineReason, setLineReason] = useState({}); // per-line reason input for Cancel Balance
  // Feb 2026 · per-line short-close qty. Blank / null = "cancel the full
  // remaining pending" (legacy behaviour); a number = partial cancel.
  const [lineQty, setLineQty] = useState({});
  // Feb 2026 · Reverse Short Close (whole doc + per-line restore) — inputs
  // are only rendered when the doc is currently Short Closed.
  const [reverseReason, setReverseReason] = useState("");
  const [restoreQty, setRestoreQty] = useState({});   // per-line "how much to restore"
  const [restoreReason, setRestoreReason] = useState({});

  const load = useCallback(async () => {
    if (!open || !docId || !docType) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/docs/${docType}/${docId}/links`);
      setProbe(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to probe downstream links");
      // Callers pass an inline `onClose` arrow that changes identity on every
      // parent re-render, so we intentionally exclude it from the deps below —
      // otherwise the reset effect fires on unrelated parent updates and wipes
      // per-line reasons the user is still typing. `load` closes over the
      // current `onClose` via ref-less lexical capture, which is fine here
      // because the probe error path only runs on real failures.
      onClose && onClose();
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, docId, docType]);

  useEffect(() => {
    // Only reset when a fresh doc / open-cycle starts. Prior versions
    // depended on `load` (which changes identity when the parent
    // re-renders) and therefore wiped the pending per-line reasons after
    // every successful Cancel Bal.
    setProbe(null); setReason(""); setMode(null); setLineReason({}); setLineQty({});
    setReverseReason(""); setRestoreQty({}); setRestoreReason({});
    if (open) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, docId, docType]);

  const runUnapprove = async () => {
    // PO-AUTH-EDIT-AMEND-H01 · Phase 2B.3 · Feb 2026 — defence in depth.
    // The Unapprove render branch is already suppressed for docType==="PO"
    // and the PO entry has been removed from UNAPPROVE_PATH, but a
    // defensive early-exit here prevents any future refactor from
    // accidentally re-enabling the retired PO bypass.
    if (docType === "PO") {
      toast.error("PO Unapprove has been retired · use Amend Order for controlled correction");
      return;
    }
    const r = (reason || "").trim();
    if (r.length < 4) { toast.error("Reason must be at least 4 characters"); return; }
    setBusy(true);
    try {
      await api.post(UNAPPROVE_PATH[docType](docId), { reason: r });
      toast.success(`${docNo || probe?.doc_no} · unapproved · back to Draft`);
      onDone && await onDone();
      onClose && onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unapprove failed");
    } finally { setBusy(false); }
  };

  const runShortClose = async () => {
    const r = (reason || "").trim();
    if (r.length < 4) { toast.error("Reason must be at least 4 characters"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/docs/${docType}/${docId}/short-close`, { reason: r });
      toast.success(`${data.doc_no} · short-closed`);
      onDone && await onDone();
      onClose && onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Short Close failed");
    } finally { setBusy(false); }
  };

  const runCancelLine = async (line) => {
    const r = (lineReason[line.key] || "").trim();
    if (r.length < 4) { toast.error("Enter a reason (≥ 4 chars) for this line"); return; }
    // Optional partial cancel — blank input means "cancel full pending".
    const rawQty = (lineQty[line.key] ?? "").toString().trim();
    let qty = null;
    if (rawQty !== "") {
      qty = Number(rawQty);
      if (!Number.isFinite(qty) || qty <= 0) { toast.error("Qty must be a positive number"); return; }
      if (qty > line.pending + 1e-6) { toast.error(`Qty ${qty} exceeds pending ${line.pending}`); return; }
    }
    setBusy(true);
    try {
      const payload = { line_key: line.key, reason: r };
      if (qty !== null) payload.qty = qty;
      const { data } = await api.post(`/docs/${docType}/${docId}/cancel-balance`, payload);
      toast.success(
        `Line ${data.line_label || line.key} · ${fmtQty(data.cancelled_qty)} cancelled` +
        (data.fully_cancelled ? "" : ` · ${fmtQty(data.remaining_after)} still open`)
      );
      setLineReason(prev => ({ ...prev, [line.key]: "" }));
      setLineQty(prev => ({ ...prev, [line.key]: "" }));
      await load();
      onLineCancel && await onLineCancel();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel balance failed");
    } finally { setBusy(false); }
  };

  // Feb 2026 · Reverse Short Close (whole doc). Restores every line's
  // pending qty from the snapshot the backend stored on short-close and
  // flips status back to the pre-close value (Approved / Authorised).
  const runReverseShortClose = async () => {
    const r = (reverseReason || "").trim();
    if (r.length < 4) { toast.error("Reason must be at least 4 characters"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/docs/${docType}/${docId}/reverse-short-close`, { reason: r });
      toast.success(`${data.doc_no} · reopened as ${data.status}`);
      onDone && await onDone();
      onClose && onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reverse Short Close failed");
    } finally { setBusy(false); }
  };

  // Feb 2026 · Reverse an earlier per-line Cancel Balance. Optional qty
  // lets the planner restore only part of a previously-cancelled amount.
  const runRestoreLine = async (line) => {
    const r = (restoreReason[line.key] || "").trim();
    if (r.length < 4) { toast.error("Enter a reason (≥ 4 chars) to restore this line"); return; }
    const rawQty = (restoreQty[line.key] ?? "").toString().trim();
    let qty = null;
    if (rawQty !== "") {
      qty = Number(rawQty);
      if (!Number.isFinite(qty) || qty <= 0) { toast.error("Qty must be a positive number"); return; }
      if (qty > line.cancelled + 1e-6) { toast.error(`Qty ${qty} exceeds cancelled ${line.cancelled}`); return; }
    }
    setBusy(true);
    try {
      const payload = { line_key: line.key, reason: r };
      if (qty !== null) payload.qty = qty;
      const { data } = await api.post(`/docs/${docType}/${docId}/reverse-short-close`, payload);
      toast.success(`Line ${data.line_label || line.key} · ${fmtQty(data.restored_qty)} restored`);
      setRestoreReason(prev => ({ ...prev, [line.key]: "" }));
      setRestoreQty(prev => ({ ...prev, [line.key]: "" }));
      await load();
      onLineCancel && await onLineCancel();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Restore failed");
    } finally { setBusy(false); }
  };
  if (!open) return null;
  const linkCounts = probe?.counts || {};
  const linkEntries = Object.entries(linkCounts).filter(([, v]) => Number(v) > 0);
  const isShortClosed = (probe?.status || "").toLowerCase() === "short closed";

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose && onClose()}>
      <DialogContent
        data-testid={`${testidPrefix}-dialog`}
        className="bg-[var(--bg-1)] border-[var(--border)] text-white max-w-[900px] max-h-[92vh] overflow-y-auto"
        onInteractOutside={(e) => busy || e.preventDefault()}
        description="Modify the selected document — the change flows into the approval queue."
      >
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-3">
            <LockOpenIcon size={18} weight="bold" className="text-amber-300" />
            Modify {DOC_LABEL[docType] || docType}
            <span className="font-mono text-[var(--accent)]">{probe?.doc_no || docNo || "…"}</span>
            {probe?.status && (
              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-sm border border-zinc-500/40 text-[10px] font-mono uppercase tracking-widest text-zinc-300">
                {probe.status}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        {loading && <div className="py-8 text-center text-sm text-zinc-400">Probing downstream links…</div>}

        {!loading && probe && (
          <div className="space-y-4">
            {/* Downstream link summary — hidden when the doc is Short
                Closed because the popover morphs into Reverse mode there
                and the link count is no longer actionable. */}
            {!isShortClosed && (
            <div
              data-testid={`${testidPrefix}-probe`}
              className={`rounded-sm border px-3 py-2.5 text-xs flex items-start gap-2.5 ${
                probe.has_links
                  ? "border-amber-500/40 bg-amber-500/5 text-amber-100"
                  : "border-emerald-500/40 bg-emerald-500/5 text-emerald-100"
              }`}
            >
              {probe.has_links
                ? <WarningIcon size={16} weight="bold" className="mt-0.5 text-amber-400 shrink-0" />
                : <InfoIcon size={16} weight="bold" className="mt-0.5 text-emerald-400 shrink-0" />}
              <div className="flex-1">
                {probe.has_links ? (
                  <>
                    <div className="font-semibold text-amber-200 text-[11px] uppercase tracking-widest">Downstream links present · Unapprove blocked</div>
                    <div className="mt-1 text-zinc-200">
                      {linkEntries.map(([k, v]) => (
                        <span key={k} className="inline-flex items-center gap-1 mr-3">
                          <LinkSimpleIcon size={12} weight="bold" className="text-amber-400" />
                          <span className="font-mono text-[11px]">{v} {k.replace(/_/g, " ")}</span>
                        </span>
                      ))}
                    </div>
                    <div className="mt-1 text-[11px] text-zinc-400">
                      Use <b>Short Close</b> to close every remaining line, or <b>Cancel Balance</b> on individual lines below.
                    </div>
                  </>
                ) : (
                  <>
                    <div className="font-semibold text-emerald-200 text-[11px] uppercase tracking-widest">No downstream links · Safe to Unapprove</div>
                    <div className="mt-1 text-[11px] text-zinc-400">
                      Unapprove will revert this doc to Draft so you can edit its lines / customer / rate again.
                    </div>
                  </>
                )}
              </div>
            </div>
            )}

            {/* Reverse Short Close · shown only when the doc is currently
                Short Closed. Restores every line's pending qty from the
                snapshot the backend stored on short-close and flips the
                status back to the pre-close value. */}
            {isShortClosed && allow.close && (
              <div className="rounded-sm border border-emerald-500/40 bg-emerald-500/5 px-3 py-3">
                <div className="text-[11px] font-mono uppercase tracking-widest text-emerald-300 mb-2 flex items-center gap-1.5">
                  <LockOpenIcon size={12} weight="bold" /> Reverse Short Close
                </div>
                <div className="text-[11px] text-zinc-400 mb-2">
                  Restore every line's pending qty and flip this doc back to
                  its pre-close status ({DOC_LABEL[docType]} will resume
                  normal downstream activity). Individual line restores are
                  disabled while the whole doc is Short Closed.
                </div>
                <textarea
                  data-testid={`${testidPrefix}-reverse-reason`}
                  rows={2}
                  value={reverseReason}
                  onChange={e => setReverseReason(e.target.value)}
                  placeholder="Reason (min 4 chars) — e.g. buyer reinstated the order"
                  className="w-full px-3 py-2 bg-[var(--bg-0)] border border-emerald-500/40 rounded-sm text-sm focus:outline-none focus:border-emerald-400"
                />
                <div className="flex justify-end mt-2">
                  <button
                    data-testid={`${testidPrefix}-reverse-confirm`}
                    disabled={busy || reverseReason.trim().length < 4}
                    onClick={runReverseShortClose}
                    className="h-9 px-4 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-mono uppercase tracking-widest rounded-sm disabled:opacity-40 flex items-center gap-1.5"
                  >
                    <LockOpenIcon size={12} weight="bold" />
                    Reverse Short Close
                  </button>
                </div>
              </div>
            )}

            {/* Action area — Unapprove OR (Short Close + per-line Cancel).
                Both are gated on the doc NOT being Short Closed — a
                closed doc only shows the Reverse block above.
                PO-AUTH-EDIT-AMEND-H01 · Phase 2B.3 · Feb 2026 — PO branch
                of Unapprove suppressed. The canonical PO correction path
                is Amend Order → Draft AMD-### → Submit for
                Re-authorisation → Management Authorise/Reject. */}
            {!isShortClosed && !probe.has_links && docType !== "PO" && allow.unapprove && (
              <div className="border border-[var(--border)] rounded-sm p-3 bg-[var(--bg-2)]/40">
                <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-400 mb-2" data-testid={`${testidPrefix}-unapprove-label`}>{docType === "WO" ? "Unapprove · Reason required" : "Unapprove (Admin)"}</div>
                <textarea
                  data-testid={`${testidPrefix}-unapprove-reason`}
                  rows={2}
                  value={reason}
                  onChange={e => setReason(e.target.value)}
                  placeholder="Reason (kept in the audit log · min 4 chars)"
                  className="w-full px-3 py-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm focus:outline-none focus:border-amber-500"
                />
                <div className="flex justify-end mt-2">
                  <button
                    data-testid={`${testidPrefix}-unapprove-confirm`}
                    disabled={busy || reason.trim().length < 4}
                    onClick={runUnapprove}
                    className="h-9 px-4 border border-amber-500/60 text-amber-200 hover:bg-amber-500/10 text-xs font-mono uppercase tracking-widest rounded-sm disabled:opacity-40"
                  >Confirm Unapprove</button>
                </div>
              </div>
            )}

            {/* PO-AUTH-EDIT-AMEND-H01 · Phase 2B.3 · Feb 2026 — PO-specific
                hint block replacing the retired Unapprove. Only rendered
                when there are no downstream links (parity with the
                suppressed branch above). */}
            {!isShortClosed && !probe.has_links && docType === "PO" && (
              <div
                data-testid={`${testidPrefix}-po-unapprove-retired`}
                className="border border-cyan-500/40 bg-cyan-500/5 rounded-sm p-3"
              >
                <div className="text-[11px] font-mono uppercase tracking-widest text-cyan-300 mb-1.5 flex items-center gap-1.5">
                  <InfoIcon size={12} weight="bold" /> PO Unapprove retired
                </div>
                <div className="text-[11px] text-zinc-300 leading-relaxed">
                  Authorised POs are no longer returned to Draft for
                  commercial correction. Use <b>Amend Order</b> from the
                  PO detail dialog to open a controlled Amendment
                  (AMD-###), edit proposed values, and submit for
                  Management Re-authorisation. The PO number and its
                  authorised revision history are preserved.
                </div>
              </div>
            )}

            {!isShortClosed && probe.has_links && allow.close && (
              <>
                {/* Short Close block */}
                <div className="border border-red-500/30 rounded-sm p-3 bg-red-500/5">
                  <div className="text-[11px] font-mono uppercase tracking-widest text-red-300 mb-2">Short Close entire {DOC_LABEL[docType]}</div>
                  <div className="text-[11px] text-zinc-400 mb-2">
                    Every line's remaining balance will be zeroed and the doc marked <span className="font-mono text-red-300">Short Closed</span>.
                    Downstream docs stay linked but no further activity is allowed.
                    <span className="block mt-1 text-amber-300/80">Need a partial reduction instead? Use the <b>Reduce Qty</b> column below to short-close only part of a line.</span>
                  </div>
                  <textarea
                    data-testid={`${testidPrefix}-short-close-reason`}
                    rows={2}
                    value={reason}
                    onChange={e => setReason(e.target.value)}
                    placeholder="Reason (min 4 chars) — e.g. Buyer cancelled remaining balance"
                    className="w-full px-3 py-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm focus:outline-none focus:border-red-500"
                  />
                  <div className="flex justify-end mt-2">
                    <button
                      data-testid={`${testidPrefix}-short-close-confirm`}
                      disabled={busy || reason.trim().length < 4}
                      onClick={runShortClose}
                      className="h-9 px-4 bg-red-500 hover:bg-red-600 text-white text-xs font-mono uppercase tracking-widest rounded-sm disabled:opacity-40 flex items-center gap-1.5"
                    >
                      <XCircleIcon size={12} weight="bold" />
                      Confirm Short Close
                    </button>
                  </div>
                </div>

                {/* Per-line Cancel Balance table */}
                <div className="border border-[var(--border)] rounded-sm overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-[var(--bg-2)]/40 text-[10px] uppercase tracking-widest text-zinc-500">
                      <tr>
                        <th className="px-2 py-2 text-left w-12">#</th>
                        <th className="px-2 py-2 text-left">Product / Line</th>
                        <th className="px-2 py-2 text-right w-20">Ordered</th>
                        <th className="px-2 py-2 text-right w-20">Done</th>
                        <th className="px-2 py-2 text-right w-20 bg-amber-500/10">Pending</th>
                        <th className="px-2 py-2 text-right w-24 bg-red-500/10">Reduce Qty</th>
                        <th className="px-2 py-2 text-left min-w-[180px]">Cancel Reason</th>
                        <th className="px-2 py-2 text-right w-28">Action</th>
                      </tr>
                    </thead>
                    <tbody data-testid={`${testidPrefix}-lines`}>
                      {(probe.lines || []).map((ln) => (
                        <tr key={ln.key} className="border-t border-[var(--border)]">
                          <td className="px-2 py-1.5 font-mono text-zinc-500">{ln.index}</td>
                          <td className="px-2 py-1.5">
                            <span className="font-mono text-white">{ln.label}</span>
                            {ln.short_closed && <span className="ml-2 text-[9px] font-mono uppercase tracking-widest text-red-400">short closed</span>}
                            {ln.cancelled > 0 && !ln.short_closed && (
                              <div className="text-[10px] text-zinc-500 mt-0.5">
                                Cancelled {fmtQty(ln.cancelled)} · {ln.cancelled_reason || "—"}
                              </div>
                            )}
                          </td>
                          <td className="px-2 py-1.5 text-right font-mono tabular-nums text-zinc-300">{fmtQty(ln.ordered)}</td>
                          <td className="px-2 py-1.5 text-right font-mono tabular-nums text-emerald-300">{fmtQty(ln.dispatched)}</td>
                          <td className="px-2 py-1.5 text-right font-mono tabular-nums text-amber-300 font-semibold">{fmtQty(ln.pending)}</td>
                          <td className="px-2 py-1.5 text-right">
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              max={ln.pending}
                              disabled={ln.pending <= 0 || busy}
                              data-testid={`${testidPrefix}-line-qty-${ln.key}`}
                              placeholder={fmtQty(ln.pending)}
                              value={lineQty[ln.key] ?? ""}
                              onChange={e => setLineQty(prev => ({ ...prev, [ln.key]: e.target.value }))}
                              className="w-full h-8 px-2 bg-[var(--bg-0)] border border-red-500/40 rounded-sm text-xs text-right tabular-nums focus:outline-none focus:border-red-400 disabled:opacity-30"
                              title="Leave blank to cancel full pending, or enter a smaller qty to short-close only part of this line"
                            />
                          </td>
                          <td className="px-2 py-1.5">
                            <input
                              type="text"
                              maxLength={140}
                              disabled={ln.pending <= 0 || busy}
                              data-testid={`${testidPrefix}-line-reason-${ln.key}`}
                              placeholder={ln.pending <= 0 ? "No balance" : "Reason (min 4 chars)"}
                              value={lineReason[ln.key] || ""}
                              onChange={e => setLineReason(prev => ({ ...prev, [ln.key]: e.target.value }))}
                              className="w-full h-8 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs focus:outline-none focus:border-amber-500 disabled:opacity-40"
                            />
                          </td>
                          <td className="px-2 py-1.5 text-right">
                            <button
                              data-testid={`${testidPrefix}-cancel-line-${ln.key}`}
                              disabled={ln.pending <= 0 || busy || (lineReason[ln.key] || "").trim().length < 4}
                              onClick={() => runCancelLine(ln)}
                              className="h-8 px-3 border border-amber-500/50 text-amber-300 hover:bg-amber-500/10 text-[10px] font-mono uppercase tracking-widest rounded-sm disabled:opacity-30 disabled:cursor-not-allowed"
                              title={lineQty[ln.key] ? `Cancel ${lineQty[ln.key]} of ${fmtQty(ln.pending)}` : `Cancel full pending ${fmtQty(ln.pending)}`}
                            >Cancel</button>
                          </td>
                        </tr>
                      ))}
                      {(probe.lines || []).length === 0 && (
                        <tr><td colSpan={8} className="px-2 py-3 text-center text-xs text-zinc-500">No line items on this document.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {/* Feb 2026 · Restore cancelled qty · appears whenever any line
                carries a residual cancellation (from a partial cancel or a
                whole-doc reverse). Rendered outside the has_links / short-
                closed branches so a PO with no GRN + cancelled lines still
                exposes the Restore action. */}
            {!isShortClosed && allow.close && (probe.lines || []).some(l => Number(l.cancelled) > 0) && (
              <div className="border border-emerald-500/30 rounded-sm overflow-x-auto">
                <div className="px-3 py-2 bg-emerald-500/5 border-b border-emerald-500/20 text-[11px] font-mono uppercase tracking-widest text-emerald-300">
                  Restore cancelled qty · reverse a per-line cancel
                </div>
                <table className="w-full text-xs">
                  <thead className="bg-[var(--bg-2)]/40 text-[10px] uppercase tracking-widest text-zinc-500">
                    <tr>
                      <th className="px-2 py-2 text-left w-12">#</th>
                      <th className="px-2 py-2 text-left">Product / Line</th>
                      <th className="px-2 py-2 text-right w-24 bg-red-500/5">Cancelled</th>
                      <th className="px-2 py-2 text-right w-24 bg-emerald-500/10">Restore Qty</th>
                      <th className="px-2 py-2 text-left min-w-[180px]">Restore Reason</th>
                      <th className="px-2 py-2 text-right w-28">Action</th>
                    </tr>
                  </thead>
                  <tbody data-testid={`${testidPrefix}-restore-lines`}>
                    {(probe.lines || []).filter(l => Number(l.cancelled) > 0).map((ln) => (
                      <tr key={`r-${ln.key}`} className="border-t border-[var(--border)]">
                        <td className="px-2 py-1.5 font-mono text-zinc-500">{ln.index}</td>
                        <td className="px-2 py-1.5"><span className="font-mono text-white">{ln.label}</span></td>
                        <td className="px-2 py-1.5 text-right font-mono tabular-nums text-red-300">{fmtQty(ln.cancelled)}</td>
                        <td className="px-2 py-1.5 text-right">
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            max={ln.cancelled}
                            disabled={busy}
                            data-testid={`${testidPrefix}-restore-qty-${ln.key}`}
                            placeholder={fmtQty(ln.cancelled)}
                            value={restoreQty[ln.key] ?? ""}
                            onChange={e => setRestoreQty(prev => ({ ...prev, [ln.key]: e.target.value }))}
                            className="w-full h-8 px-2 bg-[var(--bg-0)] border border-emerald-500/40 rounded-sm text-xs text-right tabular-nums focus:outline-none focus:border-emerald-400 disabled:opacity-30"
                            title="Leave blank to restore the entire cancelled qty on this line"
                          />
                        </td>
                        <td className="px-2 py-1.5">
                          <input
                            type="text"
                            maxLength={140}
                            disabled={busy}
                            data-testid={`${testidPrefix}-restore-reason-${ln.key}`}
                            placeholder="Reason (min 4 chars)"
                            value={restoreReason[ln.key] || ""}
                            onChange={e => setRestoreReason(prev => ({ ...prev, [ln.key]: e.target.value }))}
                            className="w-full h-8 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs focus:outline-none focus:border-emerald-500 disabled:opacity-40"
                          />
                        </td>
                        <td className="px-2 py-1.5 text-right">
                          <button
                            data-testid={`${testidPrefix}-restore-line-${ln.key}`}
                            disabled={busy || (restoreReason[ln.key] || "").trim().length < 4}
                            onClick={() => runRestoreLine(ln)}
                            className="h-8 px-3 border border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/10 text-[10px] font-mono uppercase tracking-widest rounded-sm disabled:opacity-30 disabled:cursor-not-allowed"
                            title={restoreQty[ln.key] ? `Restore ${restoreQty[ln.key]} of ${fmtQty(ln.cancelled)}` : `Restore full cancelled ${fmtQty(ln.cancelled)}`}
                          >Restore</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <button
            data-testid={`${testidPrefix}-close`}
            onClick={() => onClose && onClose()}
            className="h-9 px-4 border border-[var(--border)] hover:bg-[var(--bg-2)] text-xs font-mono uppercase tracking-widest rounded-sm"
          >Close</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
